import { useId } from 'react';

/**
 * The duct run — where a job sits in its pipeline.
 *
 * This is the signature element, and it exists to fix the single worst flaw in what the
 * business uses today: every job carried an identical status, and the state that mattered
 * was buried one level down. Here the state IS the picture.
 *
 * Drawn as a length of ductwork: two hairlines forming a channel, a joint tick at each
 * stage boundary, filled up to the current stage, with a copper node marking where the work
 * actually is. Compact enough for a table row; the same component expands on the job page.
 *
 * It reads without a legend because it is literal — the job travels the shop the way air
 * travels the duct.
 */

export type DuctStage = {
  id: string;
  name: string;
  kind: 'intake' | 'quoting' | 'scheduled' | 'in_progress' | 'awaiting_payment' | 'won' | 'lost' | 'cancelled';
  color: string;
};

type Props = {
  stages: DuctStage[];
  /** The stage the job is in. */
  currentStageId: string;
  /** `row` for tables (24px tall, no labels), `detail` for the job page (labelled). */
  variant?: 'row' | 'detail';
  /** Accessible description; falls back to the current stage name. */
  label?: string;
  className?: string;
};

/**
 * Terminal stages are not positions along the run — the work has left the duct. They render
 * as a single closed cap rather than a partially filled channel, because showing "80%
 * complete" for a cancelled job would be a lie.
 */
const TERMINAL = new Set(['won', 'lost', 'cancelled']);

export function DuctRun({ stages, currentStageId, variant = 'row', label, className }: Props) {
  const gradientId = useId();
  const current = stages.find((s) => s.id === currentStageId);
  const flowing = stages.filter((s) => !TERMINAL.has(s.kind));
  const isTerminal = current ? TERMINAL.has(current.kind) : false;

  const index = current ? flowing.findIndex((s) => s.id === current.id) : -1;
  const reached = index >= 0 ? index : flowing.length - 1;

  const height = variant === 'detail' ? 34 : 22;
  const channelTop = variant === 'detail' ? 10 : 6;
  const channelHeight = variant === 'detail' ? 14 : 10;
  const accessibleLabel = label ?? (current ? `Étape : ${current.name}` : 'Étape inconnue');

  if (isTerminal && current) {
    // A finished job gets a cap, not a run: one filled block in the stage's own colour.
    return (
      <div
        className={className}
        role="img"
        aria-label={accessibleLabel}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        <svg width={variant === 'detail' ? 64 : 40} height={height} aria-hidden="true">
          <rect
            x={0}
            y={channelTop}
            width={variant === 'detail' ? 64 : 40}
            height={channelHeight}
            rx={2}
            fill={current.color}
            opacity={current.kind === 'cancelled' ? 0.45 : 1}
          />
          {/* End cap: the run is closed off. */}
          <line
            x1={variant === 'detail' ? 63.5 : 39.5}
            y1={channelTop - 3}
            x2={variant === 'detail' ? 63.5 : 39.5}
            y2={channelTop + channelHeight + 3}
            stroke={current.color}
            strokeWidth={1.5}
          />
        </svg>
        {variant === 'detail' && (
          <span style={{ font: '600 var(--text-sm)/1.2 var(--font-body)', color: current.color }}>{current.name}</span>
        )}
      </div>
    );
  }

  const width = variant === 'detail' ? 100 : 100; // percentage-based viewBox
  const markerSize = variant === 'detail' ? 10 : 8;
  const segment = flowing.length > 1 ? width / (flowing.length - 1) : width;

  return (
    <div
      className={className}
      role="img"
      aria-label={accessibleLabel}
      style={{ width: '100%', position: 'relative' }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        aria-hidden="true"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.35" />
            <stop offset="100%" stopColor={current?.color ?? 'var(--accent)'} stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* The empty channel: two hairlines, the walls of the duct. */}
        <rect
          x={0}
          y={channelTop}
          width={width}
          height={channelHeight}
          fill="var(--surface-sunken)"
          stroke="var(--border-strong)"
          strokeWidth={0.5}
          vectorEffect="non-scaling-stroke"
        />

        {/* Air that has already passed through. */}
        <rect
          x={0}
          y={channelTop}
          width={Math.max(segment * reached, 1.5)}
          height={channelHeight}
          fill={`url(#${gradientId})`}
          style={{ transition: 'width var(--dur-move) var(--ease-out)' }}
        />

        {/* Joint ticks: where one length of duct meets the next, i.e. a stage boundary. */}
        {flowing.map((stage, i) => {
          const x = segment * i;
          const passed = i <= reached;
          return (
            <line
              key={stage.id}
              x1={x}
              y1={channelTop - 2}
              x2={x}
              y2={channelTop + channelHeight + 2}
              stroke={passed ? 'var(--text-secondary)' : 'var(--border-strong)'}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}

      </svg>

      {/*
        * Where the work actually is. Copper, because copper is the accent of this trade.
        *
        * Drawn as an element on top of the drawing rather than as an SVG `<circle>`, and that
        * is not a stylistic choice. The channel has to stretch to whatever width the column
        * gives it, which needs `preserveAspectRatio="none"` — and that scales x and y by
        * different factors, turning any circle inside into an ellipse whose flattening depends
        * on how wide the table happens to be. Positioned by percentage out here, the marker is
        * round at every width.
        */}
      {reached >= 0 && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            // `segment * reached` is already a percentage: the viewBox is 100 units wide.
            left: `${segment * reached}%`,
            top: channelTop + channelHeight / 2,
            width: markerSize,
            height: markerSize,
            marginLeft: -markerSize / 2,
            marginTop: -markerSize / 2,
            borderRadius: '50%',
            background: 'var(--accent-warm)',
            // The ring is what lifts it off the duct; it was the circle's stroke before.
            boxShadow: '0 0 0 2px var(--surface-raised)',
            transition: 'left var(--dur-move) var(--ease-out)',
            pointerEvents: 'none',
          }}
        />
      )}

      {variant === 'detail' && (
        <ol
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${flowing.length}, 1fr)`,
            listStyle: 'none',
            margin: 'var(--space-2) 0 0',
            padding: 0,
            gap: 0,
          }}
        >
          {flowing.map((stage, i) => (
            <li
              key={stage.id}
              style={{
                fontSize: 'var(--text-2xs)',
                letterSpacing: 'var(--tracking-label)',
                textTransform: 'uppercase',
                fontWeight: i === reached ? 700 : 500,
                color: i === reached ? 'var(--text-primary)' : 'var(--text-muted)',
                // Labels sit under their tick, so the first is left-aligned and the last
                // right-aligned rather than every one centred and drifting off the ends.
                textAlign: i === 0 ? 'left' : i === flowing.length - 1 ? 'right' : 'center',
              }}
            >
              {stage.name}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
